export interface PastedTextOp {
  insert: string;
  attributes?: Record<string, string>;
}

export interface PasteColorOptions {
  keepSourceColor: boolean;
  destinationColor?: string;
}

export interface PasteDelta {
  insert(text: string, attributes?: Record<string, string>): this;
}

interface PasteSourceOp {
  insert?: unknown;
  attributes?: Record<string, unknown>;
}

export function pasteTextColor(color: unknown): string | undefined {
  if (typeof color !== 'string') {
    return undefined;
  }

  const value = color.trim();
  if (!value) {
    return undefined;
  }

  return value;
}

export function isMatchStyleShortcut(event: {
  code?: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}): boolean {
  if (event.code !== 'KeyV') {
    return false;
  }

  const macMatchStyle = !!event.metaKey && !!event.altKey && !!event.shiftKey && !event.ctrlKey;
  if (macMatchStyle) {
    return true;
  }

  return !!event.ctrlKey && !!event.shiftKey && !event.altKey && !event.metaKey;
}

export function clipboardHasHtml(types: readonly string[], html: string | undefined): boolean {
  if (!types.includes('text/html')) {
    return false;
  }

  return !!html?.trim();
}

export function normalizePastedTextOps(
  sourceOps: PasteSourceOp[] | undefined,
  options: PasteColorOptions,
): PastedTextOp[] {
  const destinationColor = pasteTextColor(options.destinationColor);
  const ops: PastedTextOp[] = [];

  for (const op of sourceOps ?? []) {
    if (typeof op.insert !== 'string' || op.insert.length === 0) {
      continue;
    }

    // Block matchers insert standalone newlines. The previous paste path flattened
    // those away with textContent, so a paste in the middle of a line did not split it.
    const isLineBreak = op.insert.replace(/\n/g, '').length === 0;
    if (isLineBreak) {
      continue;
    }

    const sourceColor = pasteTextColor(op.attributes?.color);
    const color = options.keepSourceColor ? sourceColor : destinationColor;

    if (!color) {
      ops.push({ insert: op.insert });
      continue;
    }

    ops.push({ insert: op.insert, attributes: { color } });
  }

  return ops;
}

export function buildPastedDelta<T extends PasteDelta>(
  Delta: new () => T,
  sourceOps: PasteSourceOp[] | undefined,
  options: PasteColorOptions,
): T {
  const ops = normalizePastedTextOps(sourceOps, options);

  return ops.reduce((result, op) => {
    if (!op.attributes) {
      return result.insert(op.insert);
    }

    return result.insert(op.insert, op.attributes);
  }, new Delta());
}
